import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Bot } from 'lucide-react'
import type { Control } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'
import {
  VariableAutocompleteTextarea,
  type SuggestableVariable,
} from './VariableAutocompleteTextarea'
import { BASE_VARIABLES } from './agent-detail.constants'
import { AGENT_CHANNEL_VALUES, type AgentChannel } from '@super/shared-types'

const CHANNEL_LABELS: Record<AgentChannel, string> = {
  CALL: 'Voice',
  EMAIL: 'Email',
  SMS: 'SMS',
}

const COMING_SOON_CHANNELS: Record<string, Set<AgentChannel>> = {
  OUTBOUND: new Set(['SMS']),
  INBOUND: new Set(),
}

type AgentCardProps = {
  control: Control<FormValues>
  directionForDisplay: 'INBOUND' | 'OUTBOUND'
  isReceptionist: boolean
  /** Variables from the selected Trigger; drive the {{ }} autocomplete when set. */
  triggerVariables?: string[]
}

export function AgentCard({
  control,
  directionForDisplay,
  isReceptionist,
  triggerVariables,
}: AgentCardProps) {
  // When a trigger is selected, suggest the base vars + that trigger's declared
  // variables; otherwise fall back to the autocomplete's default list.
  const promptVariables: readonly SuggestableVariable[] | undefined =
    triggerVariables && triggerVariables.length > 0
      ? [
          ...BASE_VARIABLES,
          ...triggerVariables.map(name => ({
            name,
            description: 'From the selected trigger',
            required: false,
          })),
        ]
      : undefined
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bot className="w-4 h-4" />
          Agent details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={control}
          name="direction"
          render={({ field }) => <input type="hidden" {...field} />}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter agent name"
                    autoComplete="off"
                    data-1p-ignore
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isReceptionist ? (
            <FormItem>
              <FormLabel>Channel</FormLabel>
              <Select disabled value="all">
                <SelectTrigger className="w-full text-muted-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          ) : (
            <FormField
              control={control}
              name="channels"
              render={({ field }) => {
                const currentChannel = field.value?.[0] ?? 'CALL'
                const comingSoon =
                  COMING_SOON_CHANNELS[directionForDisplay] ?? new Set()
                const availableChannels = AGENT_CHANNEL_VALUES.filter(
                  ch => !comingSoon.has(ch),
                )
                return (
                  <FormItem>
                    <FormLabel>Channel</FormLabel>
                    <Select
                      value={currentChannel}
                      onValueChange={value =>
                        field.onChange([value as AgentChannel])
                      }
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a channel" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableChannels.map(channel => (
                          <SelectItem key={channel} value={channel}>
                            {CHANNEL_LABELS[channel]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )
              }}
            />
          )}
        </div>

        <FormField
          control={control}
          name="greeting"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Greeting message</FormLabel>
              <FormControl>
                {directionForDisplay === 'OUTBOUND' ? (
                  <VariableAutocompleteTextarea
                    placeholder="Hello! How can I help you today?"
                    className="min-h-[100px]"
                    variables={promptVariables}
                    {...field}
                  />
                ) : (
                  <Textarea
                    placeholder="Hello! How can I help you today?"
                    className="min-h-[100px]"
                    {...field}
                  />
                )}
              </FormControl>
              <FormDescription>
                The first message the agent will say.
                {directionForDisplay === 'OUTBOUND' && (
                  <>
                    {' '}
                    Use the format{' '}
                    <code className="rounded bg-muted px-1 py-0.5">
                      {'{{variable}}'}
                    </code>{' '}
                    for dynamic information. Example: &quot;Thank you for
                    contacting us,{' '}
                    <code className="rounded bg-muted px-1 py-0.5">
                      {'{{firstName}}'}
                    </code>
                    &quot;
                  </>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="disallowedTopics"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Off-limit topics</FormLabel>
              <FormControl>
                {directionForDisplay === 'OUTBOUND' ? (
                  <VariableAutocompleteTextarea
                    placeholder="Enter topics the agent should avoid discussing"
                    className="min-h-[120px]"
                    variables={promptVariables}
                    {...field}
                  />
                ) : (
                  <Textarea
                    placeholder="Enter topics the agent should avoid discussing"
                    className="min-h-[120px]"
                    {...field}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="prompt"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Prompt</FormLabel>
              <FormControl>
                <VariableAutocompleteTextarea
                  placeholder="You are a helpful assistant..."
                  className="min-h-[200px] text-sm"
                  variables={promptVariables}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}
